/**
 * CSV Parser - Fleet Premium
 * Utilitario para parsing de arquivos CSV de entregas
 */

/**
 * Detecta o tipo de coordenadas no header
 * @param {string} header - Linha de header do CSV
 * @returns {'latLng' | 'lngLat' | null} Formato detectado
 */
const detectCoordinateFormat = (header) => {
  const lower = header.toLowerCase();

  // Detectar ordem lat/lng
  const latIndex = Math.min(
    lower.indexOf('lat') >= 0 ? lower.indexOf('lat') : Infinity,
    lower.indexOf('latitude') >= 0 ? lower.indexOf('latitude') : Infinity,
  );
  const lngIndex = Math.min(
    lower.indexOf('lng') >= 0 ? lower.indexOf('lng') : Infinity,
    lower.indexOf('lon') >= 0 ? lower.indexOf('lon') : Infinity,
    lower.indexOf('longitude') >= 0 ? lower.indexOf('longitude') : Infinity,
  );

  if (latIndex < lngIndex) return 'latLng';
  if (lngIndex < latIndex) return 'lngLat';
  return null;
};

/**
 * Mapeia colunas do CSV para campos da entrega
 * @param {string[]} headers - Array de headers
 * @returns {Object} Mapeamento de indice para campo
 */
const mapHeaders = (headers) => {
  const mapping = {};
  const headerLower = headers.map((h) => h.toLowerCase().trim());

  // Nome
  const nameIdx = headerLower.findIndex((h) =>
    ['name', 'nome', 'cliente', 'customer', 'destinatario', 'recipient'].includes(h));
  if (nameIdx >= 0) mapping.name = nameIdx;

  // Endereco
  const addressIdx = headerLower.findIndex((h) =>
    ['address', 'endereco', 'end', 'logradouro', 'street'].includes(h));
  if (addressIdx >= 0) mapping.address = addressIdx;

  // Latitude
  const latIdx = headerLower.findIndex((h) =>
    ['lat', 'latitude', 'y'].includes(h));
  if (latIdx >= 0) mapping.lat = latIdx;

  // Longitude
  const lngIdx = headerLower.findIndex((h) =>
    ['lng', 'lon', 'longitude', 'long', 'x'].includes(h));
  if (lngIdx >= 0) mapping.lng = lngIdx;

  // Peso
  const weightIdx = headerLower.findIndex((h) =>
    ['weight', 'peso', 'kg', 'carga', 'load'].includes(h));
  if (weightIdx >= 0) mapping.weight = weightIdx;

  // Prioridade
  const priorityIdx = headerLower.findIndex((h) =>
    ['priority', 'prioridade', 'prio', 'urgency'].includes(h));
  if (priorityIdx >= 0) mapping.priority = priorityIdx;

  // Tempo de servico
  const serviceTimeIdx = headerLower.findIndex((h) =>
    ['servicetime', 'service_time', 'tempo', 'duration', 'duracao', 'time'].includes(h));
  if (serviceTimeIdx >= 0) mapping.serviceTime = serviceTimeIdx;

  // Notas
  const notesIdx = headerLower.findIndex((h) =>
    ['notes', 'notas', 'obs', 'observacoes', 'comments', 'observacao'].includes(h));
  if (notesIdx >= 0) mapping.notes = notesIdx;

  // Janela de tempo - inicio
  const timeStartIdx = headerLower.findIndex((h) =>
    ['timewindowstart', 'time_start', 'horario_inicio', 'start_time', 'hora_inicio'].includes(h));
  if (timeStartIdx >= 0) mapping.timeWindowStart = timeStartIdx;

  // Janela de tempo - fim
  const timeEndIdx = headerLower.findIndex((h) =>
    ['timewindowend', 'time_end', 'horario_fim', 'end_time', 'hora_fim'].includes(h));
  if (timeEndIdx >= 0) mapping.timeWindowEnd = timeEndIdx;

  return mapping;
};

/**
 * Valida coordenadas
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {boolean} True se validas
 */
const isValidCoordinates = (lat, lng) => {
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    !Number.isNaN(lat) &&
    !Number.isNaN(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
};

/**
 * Normaliza prioridade para valor do sistema
 * @param {string} value - Valor da prioridade
 * @returns {'low' | 'medium' | 'high'} Prioridade normalizada
 */
/**
 * Trunca e sanitiza campo de texto CSV
 */
const sanitizeField = (value, maxLength) => {
  if (!value) return '';
  return String(value).trim().slice(0, maxLength);
};

const normalizePriority = (value) => {
  if (!value) return 'medium';
  const lower = String(value).toLowerCase().trim();

  if (['high', 'alta', 'urgente', '1', 'urgent'].includes(lower)) return 'high';
  if (['low', 'baixa', '3', 'minor'].includes(lower)) return 'low';
  return 'medium';
};

/**
 * Parseia conteudo CSV de entregas
 * @param {string} content - Conteudo do arquivo CSV
 * @param {Object} options - Opcoes de parsing
 * @param {number} options.maxLines - Limite de linhas (default: 200)
 * @returns {{ deliveries: Array, errors: Array, warnings: Array }}
 */
export const parseDeliveriesCSV = (content, options = {}) => {
  const MAX_DELIVERIES = 1000;
  const { maxLines = 200 } = options;
  const effectiveMax = Math.min(maxLines, MAX_DELIVERIES);
  const deliveries = [];
  const errors = [];
  const warnings = [];

  // Separar linhas e filtrar vazias
  const lines = content
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length === 0) {
    errors.push({ line: 0, message: 'Arquivo vazio' });
    return { deliveries, errors, warnings };
  }

  // Detectar delimitador
  const firstLine = lines[0];
  const delimiter = firstLine.includes(';') ? ';' : ',';

  // Verificar se tem header
  const hasHeader = /name|nome|lat|lng|lon|latitude|longitude|address|endereco/i.test(firstLine);

  // Parsear headers
  const headers = hasHeader
    ? firstLine.split(delimiter).map((h) => h.trim().replace(/^["']|["']$/g, ''))
    : [];

  const mapping = hasHeader ? mapHeaders(headers) : {};
  const coordinateFormat = hasHeader ? detectCoordinateFormat(firstLine) : 'latLng';

  // Indice de inicio dos dados
  const dataStartIndex = hasHeader ? 1 : 0;

  // Limite de linhas
  const maxDataLines = Math.min(lines.length - dataStartIndex, effectiveMax);
  if (lines.length - dataStartIndex > effectiveMax) {
    warnings.push({
      line: 0,
      message: `Arquivo limitado a ${effectiveMax} linhas. ${lines.length - dataStartIndex - effectiveMax} linhas ignoradas.`,
    });
  }

  // Processar cada linha de dados
  for (let i = dataStartIndex; i < dataStartIndex + maxDataLines; i += 1) {
    const line = lines[i];
    const lineNumber = i + 1;

    try {
      // Parsear colunas (considerando valores entre aspas)
      const values = [];
      let current = '';
      let inQuotes = false;

      for (let j = 0; j < line.length; j += 1) {
        const char = line[j];
        if (char === '"' || char === "'") {
          inQuotes = !inQuotes;
        } else if (char === delimiter && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      // Extrair coordenadas
      let lat;
      let lng;

      if (hasHeader && mapping.lat !== undefined && mapping.lng !== undefined) {
        lat = parseFloat(values[mapping.lat]);
        lng = parseFloat(values[mapping.lng]);
      } else if (values.length >= 2) {
        // Sem header: assumir formato baseado em deteccao ou lat,lng
        const val1 = parseFloat(values[0]);
        const val2 = parseFloat(values[1]);

        if (coordinateFormat === 'lngLat') {
          lng = val1;
          lat = val2;
        } else {
          lat = val1;
          lng = val2;
        }
      }

      // Validar coordenadas
      if (!isValidCoordinates(lat, lng)) {
        errors.push({
          line: lineNumber,
          message: `Coordenadas invalidas: lat=${lat}, lng=${lng}`,
        });
        continue;
      }

      // Criar objeto de entrega
      const delivery = {
        id: `csv-${Date.now()}-${i}`,
        lat,
        lng,
        name: hasHeader && mapping.name !== undefined
          ? sanitizeField(values[mapping.name], 200)
          : '',
        address: hasHeader && mapping.address !== undefined
          ? sanitizeField(values[mapping.address], 500)
          : `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
        weight: hasHeader && mapping.weight !== undefined
          ? parseFloat(values[mapping.weight]) || undefined
          : undefined,
        priority: hasHeader && mapping.priority !== undefined
          ? normalizePriority(values[mapping.priority])
          : 'medium',
        serviceTime: hasHeader && mapping.serviceTime !== undefined
          ? (parseFloat(values[mapping.serviceTime]) || 5) * 60
          : 300, // 5 minutos default
        notes: hasHeader && mapping.notes !== undefined
          ? sanitizeField(values[mapping.notes], 1000)
          : '',
      };

      // Janela de tempo
      if (hasHeader && mapping.timeWindowStart !== undefined && mapping.timeWindowEnd !== undefined) {
        const start = values[mapping.timeWindowStart];
        const end = values[mapping.timeWindowEnd];
        if (start && end) {
          delivery.timeWindow = { start, end };
        }
      }

      deliveries.push(delivery);
    } catch (error) {
      errors.push({
        line: lineNumber,
        message: `Erro ao processar linha: ${error.message}`,
      });
    }
  }

  return { deliveries, errors, warnings };
};

/**
 * Gera CSV de exemplo para download
 * @returns {string} Conteudo CSV de exemplo
 */
export const generateExampleCSV = () => {
  const headers = ['name', 'address', 'lat', 'lng', 'weight', 'priority', 'serviceTime', 'notes'];
  const rows = [
    ['Cliente A', 'Av. Paulista, 1000, Sao Paulo', '-23.5505', '-46.6333', '10', 'high', '10', 'Entregar na recepcao'],
    ['Cliente B', 'Rua Augusta, 500, Sao Paulo', '-23.5530', '-46.6545', '5', 'medium', '5', ''],
    ['Cliente C', 'Rua Oscar Freire, 200, Sao Paulo', '-23.5620', '-46.6700', '15', 'low', '15', 'Ligar antes de chegar'],
  ];

  return [
    headers.join(','),
    ...rows.map((row) => row.map((v) => (v.includes(',') ? `"${v}"` : v)).join(',')),
  ].join('\n');
};

export default {
  parseDeliveriesCSV,
  generateExampleCSV,
};
